'use client'

import React, { type ReactNode } from 'react'
import { useLocale, useTranslations } from 'next-intl'

import { HOLIDAY_REGION, type HolidayRegion } from '@/src/constants/holidays'
import { formatHolidayPrice } from '@/src/utils/holidays'
import type { HolidayRegionSummary } from '@/src/utils/holidays/holidayRegions'

import RegionFilter, { type RegionFilterItem } from './regionFilter'

const REGION_NAME_KEYS = {
    [HOLIDAY_REGION.EUROPE]: 'regionEurope',
    [HOLIDAY_REGION.ASIA]: 'regionAsia',
    [HOLIDAY_REGION.CENTRAL_ASIA]: 'regionCentralAsia',
    [HOLIDAY_REGION.MIDDLE_EAST_AFRICA]: 'regionMiddleEastAfrica',
    [HOLIDAY_REGION.OTHER]: 'regionOther'
} as const satisfies Record<HolidayRegion, string>

interface RegionFilterWrapperProps {
    summaries: HolidayRegionSummary[]
    selected: HolidayRegion[]
    onToggle: (region: HolidayRegion) => void
    onClear: () => void
    action?: ReactNode
    isMobile: boolean
}

const RegionFilterWrapper: React.FC<RegionFilterWrapperProps> = ({
    summaries,
    selected,
    onToggle,
    onClear,
    action,
    isMobile
}) => {
    const t = useTranslations('holidaysPage')
    const locale = useLocale()

    const items: RegionFilterItem[] = summaries.map(summary => ({
        id: summary.region,
        name: t(REGION_NAME_KEYS[summary.region]),
        priceLabel: t('regionFromPrice', { price: formatHolidayPrice(summary.fromPrice, locale) }),
        countLabel: t('regionPackageCount', { count: summary.count }),
        image: summary.image,
        selected: selected.includes(summary.region)
    }))

    return (
        <RegionFilter
            label={t('regionFilterLabel')}
            clearLabel={selected.length ? t('regionClear') : undefined}
            previousLabel={t('regionScrollPrevious')}
            nextLabel={t('regionScrollNext')}
            items={items}
            onToggle={id => onToggle(id as HolidayRegion)}
            onClear={onClear}
            action={action}
            isMobile={isMobile}
        />
    )
}

export { RegionFilterWrapper }
export default RegionFilterWrapper
